import React, {useMemo} from 'react';
import {v4 as uuid} from 'uuid';
import {Box, styled} from '@mui/material';
import ProjectCard, {
  ProjectCardSkeleton,
} from '@contentlab/components/project-card';
import {
  PROJECT_CARD_MIN_WIDTH,
  RECENT_PROJECT_CARD_MAX_WIDTH,
} from '@contentlab/components/project-card/constants';
import ProjectsEmptyState from '@contentlab/components/projects-empty-state';
import {
  HOME_CONTENT_MAX_WIDTH,
  RECENT_PROJECTS_CARDS_GAP,
} from '@contentlab/config/constants';
import {ROUTES} from '@contentlab/config/routesConstants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProjectActions from '@contentlab/hooks/use-project-actions';
import ProjectAction from '@contentlab/types/ProjectAction';
import mapProjectToProjectProps from '@contentlab/utils/mapProjectToProjectProps';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import Link from '@/components/link';
import useProjectsQuery from '@/hooks/content-studio/use-projects-query';

const EmptyStateContainer = styled(Box)({
  width: '100%',
  display: 'flex',
  padding: '48px 40px',
  justifyContent: 'flex-start',
  alignItems: 'center',
  boxSizing: 'border-box',
});

const ProjectsContainer = styled(Box)({
  gap: `${RECENT_PROJECTS_CARDS_GAP}px`,
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  justifyContent: 'flex-start',
  width: '100%',
  maxWith: `${HOME_CONTENT_MAX_WIDTH}px`,
  alignItems: 'center',
});

interface RecentProjectsProps {
  projectsCount?: number;
}

function RecentProjects({projectsCount}: RecentProjectsProps) {
  const {uiInteraction} = useUiInteractionEvent();

  const {
    data: projects,
    isLoading,
    refetch,
  } = useProjectsQuery({
    sortBy: 'created_at',
    sortOrder: 'desc',
    pageSize: projectsCount,
    live: false,
  });

  const {onProjectActionClick, projectActionModals} = useProjectActions({
    interaction_location: CONTENT_LAB_VIEW_NAMES.HOME,
    flow_name: FLOW_NAMES.CONTENT_LAB.HOME,
    onActionComplete: (action: ProjectAction) => {
      switch (action) {
        case ProjectAction.Rename:
        case ProjectAction.Delete:
          refetch();
          break;
        case ProjectAction.Download:
          break;
        default:
          throw new Error(`Unknown project action: ${action}`);
      }
    },
  });

  const projectCardsData = useMemo(() => {
    return projects.map(mapProjectToProjectProps);
  }, [projects]);

  if (isLoading) {
    return (
      <ProjectsContainer>
        {Array.from({length: 3}).map((_, index) => (
          <ProjectCardSkeleton
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            data-testid={`project-card-skeleton-${index}`}
          />
        ))}
      </ProjectsContainer>
    );
  }

  if (projectCardsData.length > 0) {
    return (
      <>
        <ProjectsContainer>
          {projectCardsData.map((project) => (
            <Link
              to={`${ROUTES.CONTENT_LAB}/project/${project.id}`}
              key={project.id}
              style={{
                flex: 1,
                minWidth: PROJECT_CARD_MIN_WIDTH,
                maxWidth: RECENT_PROJECT_CARD_MAX_WIDTH,
              }}
              onClick={() => {
                uiInteraction({
                  item_name: 'project',
                  interaction_location: CONTENT_LAB_VIEW_NAMES.HOME,
                  asset_id: project.id,
                });
              }}
            >
              <ProjectCard
                project={project}
                onActionsClick={(projectProps) => {
                  const interactionId = uuid();
                  uiInteraction({
                    item_name: 'more_actions',
                    interaction_location: CONTENT_LAB_VIEW_NAMES.HOME,
                    flow_name: FLOW_NAMES.CONTENT_LAB.HOME,
                    asset_id: projectProps.id,
                    interaction_id: interactionId,
                  });
                }}
                onProjectActionClick={onProjectActionClick}
              />
            </Link>
          ))}
        </ProjectsContainer>

        {projectActionModals}
      </>
    );
  }

  return (
    <EmptyStateContainer>
      <ProjectsEmptyState />
    </EmptyStateContainer>
  );
}

export default RecentProjects;
